import { useCallback, useEffect, useRef, useState } from "react";
import { useSetting } from "./settingsHooks";
import { AsyncLock } from "core/Misc/asyncLock";
import { Deferred } from "core/Misc/deferred";
const SequencerLock = new AsyncLock();
/**
 * Creates a hook for managing teaching moment state.
 * @param name The unique name of the teaching moment.
 * @returns A hook that returns the teaching moment state.
 */
export function MakeTeachingMoment(name) {
    return (suppress) => {
        const [hasDisplayed, setHasDisplayed, resetDisplayed] = useSetting({ key: `TeachingMoments/${name}`, defaultValue: false });
        const [shouldDisplay, setShouldDisplay] = useState(false);
        const deferredRef = useRef();
        useEffect(() => {
            if (!hasDisplayed && !suppress && !deferredRef.current) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                SequencerLock.lockAsync(async () => {
                    deferredRef.current = new Deferred();
                    setShouldDisplay(true);
                    // Just hold the lock until the hook cleanup, which is effectively component unmount (e.g. the teaching moment is dismissed).
                    await deferredRef.current.promise;
                });
            }
            return () => {
                deferredRef.current?.resolve();
            };
        }, [hasDisplayed, suppress]);
        const onDismissed = useCallback(() => {
            setHasDisplayed(true);
            deferredRef.current?.resolve();
            deferredRef.current = undefined;
            setShouldDisplay(false);
        }, []);
        return {
            shouldDisplay,
            onDismissed,
            reset: resetDisplayed,
        };
    };
}
/**
 * Creates a hook for managing teaching moment state for a dialog.
 * @param name The unique name of the teaching moment.
 * @returns A hook that returns the teaching moment state for a dialog.
 */
export function MakeDialogTeachingMoment(name) {
    const useTeachingMoment = MakeTeachingMoment(name);
    return (suppress) => {
        const { shouldDisplay, onDismissed, reset } = useTeachingMoment(suppress);
        const onOpenChange = useCallback((e, data) => {
            if (!data.open) {
                onDismissed();
            }
        }, []);
        return {
            shouldDisplay,
            onOpenChange,
            reset,
        };
    };
}
/**
 * Creates a hook for managing teaching moment state for a popover.
 * @param name The unique name of the teaching moment.
 * @returns A hook that returns the teaching moment state for a popover.
 */
export function MakePopoverTeachingMoment(name) {
    const useDialogTeachingMoment = MakeDialogTeachingMoment(name);
    return (suppress) => {
        const [target, setTarget] = useState(null);
        const [positioningRef, setPositioningRef] = useState(null);
        const { shouldDisplay, onOpenChange, reset } = useDialogTeachingMoment(suppress || !target || !positioningRef);
        useEffect(() => {
            if (target && positioningRef) {
                positioningRef.setTarget(target);
            }
        }, [target, positioningRef]);
        return {
            shouldDisplay,
            positioningRef: setPositioningRef,
            targetRef: setTarget,
            onOpenChange,
            reset,
        };
    };
}
//# sourceMappingURL=teachingMomentHooks.js.map